// client/src/components/CustomerDetails.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Customer } from '../types/interfaces';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';

/** Display label for a Tilly user (email is the only identity field). */
function userLabel(email: string): string {
  const local = String(email || '').split('@')[0] || email;
  const pretty = local
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return pretty && pretty !== email ? `${pretty} (${email})` : email;
}

const emptyCustomer: Partial<Customer> = {
  name: '',
  email: '',
  address: '',
  phone: '',
  contactPerson: '',
  licenseNumber: '',
  notes: '',
  customerType: 'Retail',
  salesAssociateEmail: '',
  enabled: 1,
};

const CustomerDetails: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !customerId || customerId === 'new';
  /** List/card Edit navigates with ?edit=1 so the form opens editable. */
  const editQuery = searchParams.get('edit');
  const editFromList = useMemo(() => !isNew && editQuery === '1', [isNew, editQuery]);
  const [customer, setCustomer] = useState<Partial<Customer>>({ ...emptyCustomer });
  const [users, setUsers] = useState<{ email: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** New + ?edit=1 start editable; otherwise read-only until Edit Customer. */
  const [editing, setEditing] = useState(isNew || editFromList);
  const [snapshot, setSnapshot] = useState<Partial<Customer> | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/users/directory`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`HTTP ${res.status}: ${text.slice(0, 50)}`);
        }
        const data = await res.json();
        setUsers(Array.isArray(data) ? data : []);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('[CustomerDetails] Fetch users directory error:', err);
        setError('Failed to load sales associates: ' + errorMessage);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (isNew) {
      setEditing(true);
      setCustomer({ ...emptyCustomer });
      setSnapshot(null);
      return;
    }
    const startEditing = editQuery === '1';
    const fetchCustomer = async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/customers/${customerId}`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`HTTP ${res.status}: ${text.slice(0, 50)}`);
        }
        const data = await res.json();
        console.log('[CustomerDetails] Fetched customer:', data);
        const loaded: Partial<Customer> = {
          customerId: data.customerId,
          name: data.name || '',
          email: data.email || '',
          address: data.address || '',
          phone: data.phone || '',
          contactPerson: data.contactPerson || '',
          licenseNumber: data.licenseNumber || '',
          notes: data.notes || '',
          customerType: data.customerType || 'Retail',
          salesAssociateEmail: data.salesAssociateEmail || '',
          enabled: data.enabled ?? 1,
        };
        setCustomer(loaded);
        setSnapshot(loaded);
        // Honor ?edit=1 from list Edit; otherwise stay read-only.
        setEditing(startEditing);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('[CustomerDetails] Fetch error:', err);
        setError('Failed to load customer: ' + errorMessage);
      }
    };
    fetchCustomer();
  }, [customerId, isNew, editQuery]);

  const handleSave = async () => {
    if (!customer.name || !customer.email) {
      setError('Name and email are required');
      return;
    }
    setSaving(true);
    try {
      const method = isNew ? 'POST' : 'PATCH';
      const url = isNew
        ? `${API_BASE_URL}/api/customers`
        : `${API_BASE_URL}/api/customers/${customerId}`;
      const payload = {
        name: customer.name,
        email: customer.email,
        address: customer.address || '',
        phone: customer.phone || '',
        contactPerson: customer.contactPerson || '',
        licenseNumber: customer.licenseNumber || '',
        notes: customer.notes || '',
        customerType: customer.customerType || 'Retail',
        salesAssociateEmail: customer.salesAssociateEmail || null,
        enabled: customer.enabled ?? 1,
      };
      console.log('[CustomerDetails] Saving customer:', { method, url, payload });
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      let data: Record<string, unknown> = {};
      try {
        data = await res.json();
      } catch {
        /* non-JSON */
      }
      if (!res.ok) {
        const msg =
          (typeof data.error === 'string' && data.error) ||
          `HTTP ${res.status}`;
        throw new Error(msg);
      }
      console.log('[CustomerDetails] Saved customer:', data);
      setSuccessMessage(isNew ? 'Customer created successfully' : 'Customer updated successfully');
      setError(null);
      setTimeout(() => {
        setSuccessMessage(null);
        navigate('/customers');
      }, 1500);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[CustomerDetails] Save error:', err);
      setError('Failed to save customer: ' + errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isNew) {
      navigate('/customers');
      return;
    }
    // List/card Edit (?edit=1): abandon and return to the list.
    if (editing && editFromList) {
      navigate('/customers');
      return;
    }
    // In-page Edit Customer: revert to read-only detail.
    if (editing) {
      if (snapshot) setCustomer(snapshot);
      setEditing(false);
      setError(null);
      return;
    }
    navigate('/customers');
  };

  const associateOptions = [...users];
  const currentAssociate = String(customer.salesAssociateEmail || '').trim();
  if (currentAssociate && !associateOptions.some((u) => u.email === currentAssociate)) {
    associateOptions.push({ email: currentAssociate });
  }

  const readOnly = !editing;

  return (
    <div className="page-container container">
      <h2 className="app-header mb-4">
        {isNew ? 'Add Customer' : editing ? 'Edit Customer' : 'Customer'}
      </h2>
      {error && (
        <div className="alert alert-danger" data-testid="customer-error">
          {error}
        </div>
      )}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      <div className="customer-form">
        <label className="form-label">
          Name (required):
          <input
            type="text"
            value={customer.name || ''}
            onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
            placeholder="Enter customer name"
            className="form-control"
            disabled={readOnly}
            data-testid="customer-name"
          />
        </label>
        <label className="form-label">
          Email (required):
          <input
            type="email"
            value={customer.email || ''}
            onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
            placeholder="Enter email"
            className="form-control"
            disabled={readOnly}
            data-testid="customer-email"
          />
        </label>
        <label className="form-label">
          Phone:
          <input
            type="text"
            value={customer.phone || ''}
            onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
            placeholder="Enter phone number"
            className="form-control"
            disabled={readOnly}
          />
        </label>
        <label className="form-label">
          Contact Person:
          <input
            type="text"
            value={customer.contactPerson || ''}
            onChange={(e) => setCustomer({ ...customer, contactPerson: e.target.value })}
            placeholder="Enter contact person"
            className="form-control"
            disabled={readOnly}
          />
        </label>
        <label className="form-label">
          License Number:
          <input
            type="text"
            value={customer.licenseNumber || ''}
            onChange={(e) => setCustomer({ ...customer, licenseNumber: e.target.value })}
            placeholder="Enter license number"
            className="form-control"
            disabled={readOnly}
          />
        </label>
        <label className="form-label">
          Customer Type:
          <select
            value={customer.customerType || 'Retail'}
            onChange={(e) => setCustomer({ ...customer, customerType: e.target.value })}
            className="form-control"
            disabled={readOnly}
            data-testid="customer-type"
          >
            <option value="Distributor">Distributor (Tax Free)</option>
            <option value="Retail">Retail</option>
            <option value="Taproom">Taproom</option>
            <option value="Other">Other</option>
          </select>
        </label>
        <label className="form-label">
          Sales Associate:
          <select
            value={customer.salesAssociateEmail || ''}
            onChange={(e) =>
              setCustomer({ ...customer, salesAssociateEmail: e.target.value || '' })
            }
            className="form-control"
            aria-label="Sales Associate"
            disabled={readOnly}
            data-testid="customer-sales-associate"
          >
            <option value="">(none)</option>
            {associateOptions.map((u) => (
              <option key={u.email} value={u.email}>
                {userLabel(u.email)}
              </option>
            ))}
          </select>
        </label>
        <label className="form-label">
          Address:
          <input
            type="text"
            value={customer.address || ''}
            onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
            placeholder="Enter address"
            className="form-control"
            disabled={readOnly}
          />
        </label>
        <label className="form-label">
          Notes:
          <textarea
            value={customer.notes || ''}
            onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
            placeholder="Enter any notes"
            className="form-control"
            disabled={readOnly}
          />
        </label>
        <label className="form-label">
          Enabled:
          <input
            type="checkbox"
            checked={customer.enabled === 1}
            onChange={(e) => setCustomer({ ...customer, enabled: e.target.checked ? 1 : 0 })}
            className="ms-2"
            disabled={readOnly}
          />
        </label>
      </div>
      <div
        className="inventory-actions mt-4 d-flex flex-wrap gap-2"
        style={{ gap: '10px' }}
      >
        {!isNew && !editing && (
          <button
            type="button"
            onClick={() => {
              setSnapshot(customer);
              setEditing(true);
              setError(null);
            }}
            className="btn btn-primary"
            style={{ minHeight: '44px' }}
            data-testid="customer-edit"
          >
            Edit Customer
          </button>
        )}
        {editing && (
          <button
            type="button"
            onClick={handleSave}
            className="btn btn-primary"
            style={{ minHeight: '44px' }}
            disabled={saving}
            data-testid="customer-save"
          >
            {saving ? 'Saving…' : 'Save Customer'}
          </button>
        )}
        <button
          type="button"
          onClick={handleCancel}
          className="btn btn-danger"
          style={{ minHeight: '44px' }}
          disabled={saving}
          data-testid="customer-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default CustomerDetails;
